import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Plus from "assets/img/svg/plus";
import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import TitleCover from "components/shared/TitleCover";
import TermTemplateForm from "components/TermTemplates/TermTemplateForm";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchAgreements } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { PressTemplateConfiguration, TermTemplate } from "store/termTemplatesStore";
import { deleteTermTemplate, fetchBeneficiaryFrameworkContracts } from "store/termTemplatesStore";

import { buildRecapSubtitle, FrameworkContractRecapContent } from "./RecapModal";

interface Props {
    beneficiaryId: number;
    beneficiaryFullName: string;
    beneficiaryEmail?: string | null;
    /**
     * When set, the create modal is controlled by the parent. Otherwise the component
     * manages its own create modal state (opened from the internal "Add" button).
     */
    createModalOpen?: boolean;
    onCreateModalOpenChange?: (open: boolean) => void;
}

const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 600,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "8px 16px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "nowrap",
    zIndex: 0,
};

const tBodyStyle = {
    fontWeight: 400,
    fontSize: "0.75rem",
    fontFamily: "'Poppins', sans-serif",
    padding: "8px 16px",
};

const BeneficiaryFrameworkContracts = ({
    beneficiaryId,
    beneficiaryFullName,
    beneficiaryEmail,
    createModalOpen,
    onCreateModalOpenChange,
}: Props) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const portalType = useContext(PortalType);
    const activeSignProvider = useActiveSignProvider();
    const { getCompanySettings } = useCompanySettings();
    const usesAgreements = !!getCompanySettings("uses_agreements");
    const isPress = portalType === "press" || portalType === "audiovisual";
    const { beneficiaryFrameworkContracts, beneficiaryFrameworkContractsStatus } = useAppSelector(
        (state) => state.termTemplates,
    );

    const isCreateControlled = createModalOpen !== undefined;
    const [modalType, setModalType] = useState<"edit" | "delete" | "recap" | "">("");
    const [chosenTemplate, setChosenTemplate] = useState<TermTemplate | undefined>(undefined);
    const [internalCreateOpen, setInternalCreateOpen] = useState(false);

    const showCreateModal = isCreateControlled ? !!createModalOpen : internalCreateOpen;
    const openCreateModal = () => {
        if (isCreateControlled) onCreateModalOpenChange?.(true);
        else setInternalCreateOpen(true);
    };
    const closeCreateModal = () => {
        if (isCreateControlled) onCreateModalOpenChange?.(false);
        else setInternalCreateOpen(false);
    };

    useEffect(() => {
        dispatch(fetchBeneficiaryFrameworkContracts({ beneficiaryId }));
    }, [beneficiaryId, dispatch]);

    useEffect(() => {
        if (usesAgreements) dispatch(fetchAgreements());
    }, [usesAgreements, dispatch]);

    const { agreements } = useAppSelector((state) => state.agreements);

    const handleEdit = (template: TermTemplate) => {
        setChosenTemplate(template);
        setModalType("edit");
    };

    const handleDeleteClick = (template: TermTemplate) => {
        setChosenTemplate(template);
        setModalType("delete");
    };

    const handleDelete = async () => {
        if (chosenTemplate) {
            const response = await dispatch(deleteTermTemplate({ templateId: chosenTemplate.id }));
            if (response.meta.requestStatus === "fulfilled") {
                setModalType("");
                setChosenTemplate(undefined);
            }
        }
    };

    const closeModal = () => {
        setModalType("");
    };

    const navigateToNewAgreement = (template: TermTemplate, documentType: "framework_contract" | "avenant") => {
        const state: NewAgreementLocationState = {
            modalType: "single",
            documentType,
            signatureProvider: activeSignProvider.key,
            relatedResourceData: {
                relatedResource: "frameworkContract",
                relatedResourceId: template.id,
                beneficiaryId,
                titleId: 0,
                title: template.name,
                beneficiary: beneficiaryFullName,
                email: beneficiaryEmail ?? undefined,
            },
        };
        navigate("/agreements/new", { state });
    };

    const buildRowOptions = (template: TermTemplate): ThreeDotMenuOptions[] => [
        {
            id: "send-for-signature",
            name: t("TermTemplates.SendForSignature"),
            action: () => navigateToNewAgreement(template, "framework_contract"),
            hide: !usesAgreements,
        },
        {
            id: "create-avenant",
            name: t("TermTemplates.CreateAvenant"),
            action: () => navigateToNewAgreement(template, "avenant"),
            hide: !usesAgreements,
        },
        { id: "edit", name: t("TermTemplates.Edit"), action: () => handleEdit(template) },
        { id: "delete", name: t("TermTemplates.Delete"), action: () => handleDeleteClick(template) },
    ];

    const openRecap = (template: TermTemplate) => {
        setChosenTemplate(template);
        setModalType("recap");
    };

    const isLoading = beneficiaryFrameworkContractsStatus === "pending" && !beneficiaryFrameworkContracts.length;

    return (
        <div>
            {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                    <Spinner />
                </div>
            ) : (
                <Paper
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        marginBottom: "8px",
                        border: "1px solid #EFEFEF",
                        borderRadius: "8px",
                        boxShadow: "none",
                    }}
                >
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tHeadStyles}>{t("Beneficiars.FrameworkContractColumn")}</TableCell>
                                    {isPress ? (
                                        <>
                                            <TableCell sx={tHeadStyles}>{t("TermTemplates.PressFolioPrice")}</TableCell>
                                            <TableCell sx={tHeadStyles}>{t("TermTemplates.PressWorkNature")}</TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell sx={tHeadStyles}>
                                                {t("Beneficiars.FrameworkChannelsColumn")}
                                            </TableCell>
                                            <TableCell sx={tHeadStyles}>
                                                {t("Beneficiars.FrameworkAdvanceColumn")}
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell sx={tHeadStyles}>{t("TermTemplates.StartDate")}</TableCell>
                                    <TableCell sx={tHeadStyles}>{t("TermTemplates.EndDate")}</TableCell>
                                    <TableCell sx={tHeadStyles} align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {beneficiaryFrameworkContracts.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={6}
                                            sx={{
                                                textAlign: "center",
                                                color: "#7C7C82",
                                                fontSize: "13px",
                                                padding: "40px 16px",
                                            }}
                                        >
                                            {t("Beneficiars.NoFrameworkContract")}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    beneficiaryFrameworkContracts.map((template) => {
                                        const channelsCount = template.configuration?.channels?.length ?? 0;
                                        const advance = (template.configuration?.advancePayment ?? 0) / 100;
                                        const pressConfig =
                                            template.configuration as unknown as PressTemplateConfiguration;
                                        const pressFolioPrice = (pressConfig?.folioPrice ?? 0) / 100;
                                        const createdAt = template.createdAt ?? template.modifiedAt;
                                        // Dates stored as "yyyy-MM-dd" — see form. timeZone: "UTC" preserves the day.
                                        const startDateRaw = template.configuration?.startDate;
                                        const endDateRaw = template.configuration?.endDate;
                                        const formatDay = (raw: string) =>
                                            formatLocaleDateString({
                                                date: raw as unknown as Date,
                                                options: { numeric: true, timeZone: "UTC" },
                                            });
                                        return (
                                            <TableRow
                                                key={template.id}
                                                hover
                                                onClick={() => openRecap(template)}
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <TableCell style={tBodyStyle}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "12px",
                                                        }}
                                                    >
                                                        <TitleCover
                                                            title={template.name}
                                                            variant="framework"
                                                            style={{ minWidth: "32px", maxWidth: "32px" }}
                                                        />
                                                        <div>
                                                            <div
                                                                style={{
                                                                    fontSize: "0.8125rem",
                                                                    fontWeight: 500,
                                                                    marginBottom: "2px",
                                                                }}
                                                            >
                                                                {template.name}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    color: "#8C8C92",
                                                                    fontSize: "0.6875rem",
                                                                }}
                                                            >
                                                                créé le :{" "}
                                                                {formatLocaleDateString({
                                                                    date: createdAt as Date,
                                                                    options: { numeric: true, timeZone: "UTC" },
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                {isPress ? (
                                                    <>
                                                        <TableCell
                                                            style={{
                                                                ...tBodyStyle,
                                                                fontVariantNumeric: "tabular-nums",
                                                            }}
                                                        >
                                                            {formatNumber(pressFolioPrice, {
                                                                isCurrency: true,
                                                                always2Digit: true,
                                                            })}
                                                        </TableCell>
                                                        <TableCell style={tBodyStyle}>
                                                            {pressConfig?.workNature || "—"}
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell style={tBodyStyle}>
                                                            {t("Beneficiars.FrameworkChannelsCount", {
                                                                count: channelsCount,
                                                            })}
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                ...tBodyStyle,
                                                                fontVariantNumeric: "tabular-nums",
                                                            }}
                                                        >
                                                            {formatNumber(advance, {
                                                                isCurrency: true,
                                                                always2Digit: true,
                                                            })}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell
                                                    style={{ ...tBodyStyle, fontVariantNumeric: "tabular-nums" }}
                                                >
                                                    {startDateRaw ? formatDay(startDateRaw) : "—"}
                                                </TableCell>
                                                <TableCell
                                                    style={{ ...tBodyStyle, fontVariantNumeric: "tabular-nums" }}
                                                >
                                                    {endDateRaw ? formatDay(endDateRaw) : "—"}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ width: "40px" }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ThreeDotMenu options={buildRowOptions(template)} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <StyledButton
                sx={{
                    gap: "8px",
                    padding: "4px 16px",
                    background: "white",
                    color: "#E0DEE0",
                    width: "100%",
                    marginTop: "1rem",
                }}
                onClick={openCreateModal}
                buttonType="secondary"
            >
                <Plus />
                {t("Beneficiars.AddFrameworkContract")}
            </StyledButton>

            <ModalBase
                open={showCreateModal}
                onClose={closeCreateModal}
                modalTitle={t("TermTemplates.CreateFrameworkContractTitle")}
                modalSubtitle={t("TermTemplates.FrameworkContractSubtitle", { beneficiary: beneficiaryFullName })}
                widthTransition={{ width: "700px", active: true }}
                contentStyle={{ paddingInline: "0px", paddingBlock: "24px 0px" }}
            >
                <TermTemplateForm mode="new" onClose={closeCreateModal} beneficiaryId={beneficiaryId} />
            </ModalBase>

            <ModalBase
                open={modalType === "edit" || modalType === "recap"}
                onClose={closeModal}
                modalTitle={
                    modalType === "recap" ? chosenTemplate?.name ?? "" : t("TermTemplates.EditFrameworkContractTitle")
                }
                modalSubtitle={
                    modalType === "recap"
                        ? buildRecapSubtitle(t, chosenTemplate)
                        : t("TermTemplates.FrameworkContractSubtitle", { beneficiary: beneficiaryFullName })
                }
                widthTransition={{
                    width: modalType === "recap" ? "760px" : "700px",
                    active: true,
                }}
                contentStyle={
                    modalType === "recap" ? { padding: 0 } : { paddingInline: "0px", paddingBlock: "24px 0px" }
                }
            >
                {modalType === "recap" && chosenTemplate ? (
                    <FrameworkContractRecapContent
                        template={chosenTemplate}
                        onClose={closeModal}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        signatureContext={{
                            beneficiaryId,
                            agreements,
                            onCreateAgreement: navigateToNewAgreement,
                        }}
                    />
                ) : (
                    <TermTemplateForm
                        mode="edit"
                        template={chosenTemplate}
                        onClose={closeModal}
                        beneficiaryId={beneficiaryId}
                    />
                )}
            </ModalBase>

            <DeleteModal
                open={modalType === "delete"}
                onClose={closeModal}
                onDelete={handleDelete}
                elementTitle={t("TermTemplates.TheFrameworkContract", {
                    templateName: chosenTemplate?.name ?? "",
                })}
            />
        </div>
    );
};

export default BeneficiaryFrameworkContracts;
